import React, { useState } from 'react'
import { Stack, Typography, Button, Box, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'

import CreditSelector from './CreditSelector'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat, roundTo2DP } from '@/helpers/numbers'
import useTenant from '@/hooks/useTenant'

const canteenCartTopUpContentUserDataFragment = graphql(`
  fragment CanteenCartTopUpContentV2_UserDataFragment on User {
    wallet {
      balance
    }
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

type Props = {
  loading: boolean
  onCheckoutClicked: (bundle: number) => void
  cartTotal: number
  userData: FragmentType<typeof canteenCartTopUpContentUserDataFragment>
  availableSubsidy: number
  currency: string
}

const Content: React.FC<Props> = ({
  loading,
  onCheckoutClicked,
  cartTotal,
  userData: userDataProp,
  availableSubsidy,
  currency,
}) => {
  const { t } = useTranslation()
  const tenant = useTenant()
  const userData = useFragment(canteenCartTopUpContentUserDataFragment, userDataProp)
  const minimumTopupValue = tenant.features?.ccTopupAmounts?.minimumTopup ?? 0
  const totalToPay = roundTo2DP(cartTotal - availableSubsidy - userData.wallet.balance)
  const [bundle, setBundle] = useState(totalToPay < minimumTopupValue ? minimumTopupValue : 0)

  const handleBundleChange = (newBundle: number) => {
    setBundle(newBundle)
  }

  const checkout = () => {
    onCheckoutClicked(bundle)
  }

  const hidePrices = userData?.account?.userInterfaceSettings.hideCCPrices
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          position: 'relative',
        }}>
        <Stack spacing={5} paddingX={3} direction="column" height="100%">
          <Stack spacing={2} direction="column" alignItems="flex-start">
            <Typography variant="subtitle2">{t('cc:addFunds')}</Typography>
            <Typography color="text.secondary" variant="body2">
              {t('cc:addFundsDescription')}
            </Typography>
            {hidePrices ? null : (
              <Chip
                label={t('cc:currentCreditBalance', {
                  balance: numberFormat(userData.wallet.balance),
                })}
                sx={{
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  width: '100%',
                }}
              />
            )}
          </Stack>
          <CreditSelector
            currency={currency}
            bundle={bundle}
            onChange={handleBundleChange}
            totalToPay={totalToPay}
          />
        </Stack>
      </Box>
      <Stack bgcolor="background.paper" paddingX={3} paddingY={2}>
        <Button
          variant="contained"
          size="medium"
          fullWidth
          onClick={checkout}
          disabled={loading || (totalToPay < minimumTopupValue && bundle === 0)}>
          {t('cc:orderNow')}
        </Button>
      </Stack>
    </>
  )
}

export default Content
